import { getCPUData, getGPUData } from '../../../../shared/deviceCapabilities';
import { type Plugin } from '../../../pluggableService/pluggableService';
import { type CallService } from '../../interface';
import { getLogger } from '../../../../libs/logger';

/**
 * Callservice BenchmarkPlugin.
 * It runs CPU and GPU benchmarks when the call is in an idle state.
 *
 * Implements the Plugin interface for CallService
 */
export class BenchmarkPlugin implements Plugin<CallService, object> {
  name = 'BenchmarkPlugin';

  private readonly cpu: boolean;

  private readonly gpu: boolean;

  private benchmarkPromise?: Promise<void>;

  constructor({ cpu, gpu }: { gpu: boolean; cpu: boolean }) {
    this.cpu = cpu;
    this.gpu = gpu;
  }

  apply<T extends CallService>(callService: T): T {
    if (typeof window === 'undefined') {
      return callService;
    }
    void this.runBenchmark();
    return callService;
  }

  private async runBenchmark(): Promise<void> {
    if (this.benchmarkPromise) {
      await this.benchmarkPromise;
      return;
    }

    this.benchmarkPromise = (async () => {
      try {
        const logger = getLogger('CallService - Benchmark');
        let cpuScore = undefined;
        let gpuScore = undefined;
        if (this.cpu) {
          cpuScore = await getCPUData();
          logger.info(`CPU Benchmark Score:`, cpuScore);
        }
        if (this.gpu) {
          gpuScore = await getGPUData();
          logger.info(`GPU Benchmark Score:`, gpuScore);
        }
      } catch (error) {
        console.error('Benchmark failed:', error);
      } finally {
        this.benchmarkPromise = undefined;
      }
    })();

    await this.benchmarkPromise;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  destroy(): void {}
}
